import type { ModuleId, WeaponId } from "./definitions.js";
import type { PassiveId } from "./contracts.js";
import type { EquipmentTier } from "./equipment-tiers.js";
import { SHIP_SYSTEMS, type ShipSystemId } from "./ship-systems.js";

export type EquipmentKey =
  | { kind: "weapon"; id: WeaponId }
  | { kind: "module"; id: ModuleId }
  | { kind: "passive"; id: Exclude<PassiveId, "none"> }
  | { kind: "system"; id: ShipSystemId };
export type LegacyEquipmentKey = Exclude<EquipmentKey, { kind: "system" }>;
export interface EquipmentStack {
  key: EquipmentKey;
  tier: EquipmentTier;
  count: number;
}
export interface Arsenal {
  knownBlueprints: EquipmentKey[];
  stacks: EquipmentStack[];
}
export interface EquipmentDefinition {
  key: EquipmentKey;
  name: string;
  recipeComponents: number;
  dismantleComponents: number;
  blueprint: {
    contractId: "hunt" | "salvage" | "signal";
    successes: number;
  } | null;
}

function entry(
  key: EquipmentKey,
  name: string,
  recipeComponents: number,
  contractId: "hunt" | "salvage" | "signal" | null,
): EquipmentDefinition {
  return {
    key,
    name,
    recipeComponents,
    dismantleComponents: Math.floor(recipeComponents / 2),
    blueprint: contractId ? { contractId, successes: 1 } : null,
  };
}
export const EQUIPMENT: readonly EquipmentDefinition[] = [
  entry({ kind: "system", id: "reactor" }, SHIP_SYSTEMS.reactor.name, SHIP_SYSTEMS.reactor.recipeComponents, null),
  entry({ kind: "system", id: "shield" }, SHIP_SYSTEMS.shield.name, SHIP_SYSTEMS.shield.recipeComponents, null),
  entry({ kind: "system", id: "engine" }, SHIP_SYSTEMS.engine.name, SHIP_SYSTEMS.engine.recipeComponents, null),
  entry({ kind: "weapon", id: "pulse" }, "Импульсная пушка", 2, null),
  entry({ kind: "weapon", id: "laser" }, "Лазер", 4, "hunt"),
  entry({ kind: "weapon", id: "missile" }, "Ракетная установка", 6, "salvage"),
  entry({ kind: "weapon", id: "flak" }, "Картечная пушка", 8, "hunt"),
  entry({ kind: "weapon", id: "rail" }, "Рельсотрон", 10, "hunt"),
  entry({ kind: "weapon", id: "ion" }, "Ионный излучатель", 12, "salvage"),
  entry({ kind: "module", id: "barrier" }, "Энергетический барьер", 3, null),
  entry({ kind: "module", id: "repair" }, "Аварийный ремонт", 3, null),
  entry({ kind: "module", id: "volley" }, "Усиленный залп", 4, null),
  entry({ kind: "module", id: "mine" }, "Мина", 4, "hunt"),
  entry({ kind: "module", id: "jammer" }, "Глушилка наведения", 6, "hunt"),
  entry({ kind: "module", id: "interceptor" }, "Перехват ракет", 6, "hunt"),
  entry({ kind: "module", id: "repair-field" }, "Ремонтное поле", 8, "salvage"),
  entry(
    { kind: "module", id: "scan" },
    "Разведывательный сканер",
    8,
    "salvage",
  ),
  entry({ kind: "passive", id: "targeting" }, "Система наведения", 6, "hunt"),
  entry(
    { kind: "passive", id: "capacitor" },
    "Накопитель энергии",
    6,
    "salvage",
  ),
  entry({ kind: "passive", id: "repair-drone" }, "Ремонтный дрон", 8, "signal"),
  entry(
    { kind: "passive", id: "thrusters" },
    "Маневровые двигатели",
    10,
    "signal",
  ),
];
export const STARTER_EQUIPMENT: readonly EquipmentKey[] = [
  { kind: "weapon", id: "pulse" },
  { kind: "module", id: "barrier" },
  { kind: "module", id: "repair" },
];
export function sameEquipmentKey(
  left: EquipmentKey,
  right: EquipmentKey,
): boolean {
  return left.kind === right.kind && left.id === right.id;
}
export function equipmentDefinition(key: EquipmentKey): EquipmentDefinition {
  const definition = EQUIPMENT.find((item) => sameEquipmentKey(item.key, key));
  if (!definition)
    throw new Error(`Equipment definition missing: ${key.kind}:${key.id}`);
  return definition;
}
